import styles from "./SearchPage.module.scss";
import SideBar from "../../components/sideBar/SideBar";
import Input from "../../components/shared/Input";
import React, { useEffect, useMemo, useState } from "react";
import PeriodModule from "../filters/components/PeriodModule";
import useInterfaceContext from "../../components/providers/interface";
import {
  useGetSearchFilmsListQuery,
  useGetAISearchFilmsListQuery,
  useGetPersonsListQuery,
} from "../../store/api/filmAPI";
import GenresModule from "../filters/components/GenresModule";
import MoviesList from "./MoviesList/MoviesList";
import ActorsList from "./ActorsList";
import Footer from "../../components/footer/Footer";
import useWindowSize from "../../hooks/useWindowSize";
import Loader from "../../components/loader/Loader";
import Header from "../../components/header/Header";
import { useTranslation } from "react-i18next";
import SearchFieldIcon from "../../components/icons/searchIcons/searchField";
import { languageHandler } from "../../utils/helpers";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setSearch } from "../../store/slices/filters.slice";
import { useAppSelector } from "../../store/store";
import useLikeClick from "../../hooks/useLikeClick";
import useFavouriteClick from "../../hooks/useFavouriteClick";
import useActorFavouriteClick from "../../hooks/useActorFavouriteClick";
import { ROUTES } from "../../constants/routes";
import {
  cleanUpPage,
  setDataToList,
  setGenresValue,
  setPage,
  setPeriodValue,
  setPeriodValueUI,
  setPrevPage,
  setSortBy,
} from "../../store/slices/lists.slice";
import LoginModal from "../../components/shared/LoginModal/LoginModal";
import { genresData } from "../../constants/structure";
import GradientBackdrop from "../../components/shared/GradientBackdrop/GradientBackdrop";

const movieSortList = [
  { title: "Rating", sort: "rating.desc" },
  { title: "IMDb", sort: "imdb.desc" },
  { title: "date", sort: "release_date.desc" },
];
const actorSortList = [
  { title: "Popularity", sort: "popularity.desc" },
  { title: "recommended", sort: "name.asc" },
];
const AI_SEARCH_PAGE_SIZE = 20;

type SearchTab = "movies" | "actors";

interface IQuery {
  language: string;
  sort_by: string;
  include_video: boolean;
  release_date_gte?: string;
  release_date_lte?: string;
  with_genres?: string;
}

const SearchPage = () => {
  const search = useAppSelector((state: any) => state.filters.search);
  const filmList = useAppSelector((state: any) => state.list.commonList);
  const page = useAppSelector((state: any) => state.list.page);
  const sortBy = useAppSelector((state: any) => state.list.sortBy);
  const periodValue = useAppSelector((state: any) => state.list.periodValue);
  const periodValueUI = useAppSelector((state: any) => state.list.periodValueUI);
  const genresValue = useAppSelector((state: any) => state.list.genresValue);
  const prevPage = useAppSelector((state: any) => state.list.prevPage);
  const maxYear = useAppSelector((state: any) => state.filters.maxYear);
  const { loginModal } = useAppSelector((state: any) => state.modals);

  const [searchValue, setSearchValue] = useState("");
  const [inputValue, setInputValue] = useState(search || "");
  const [activeChips, setActiveChips] = useState(
    movieSortList.find((item) => item.sort === sortBy?.sort)?.title || "Rating",
  );
  const [activeTab, setActiveTab] = useState<SearchTab>("movies");
  const [actorSortBy, setActorSortBy] = useState(actorSortList[0]);
  const [actorsPage, setActorsPage] = useState(1);
  const [actorsPrevPage, setActorsPrevPage] = useState(0);
  const [actorsList, setActorsList] = useState<any[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const { isMobile } = useInterfaceContext();
  const { windowSize } = useWindowSize();
  const { t, i18n } = useTranslation();
  const { likesList, likeHandler } = useLikeClick();
  const { favoritesList, favouritesHandler } = useFavouriteClick();
  const { actorFavoritesList, actorFavouritesHandler } = useActorFavouriteClick();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    setActiveTab("movies");
  }, [location.key]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const clearGenresHandler = () => {
    dispatch(cleanUpPage());
    dispatch(setGenresValue([]));
  };

  const clearPeriodHandler = () => {
    dispatch(cleanUpPage());
    dispatch(setPeriodValue([1980, maxYear]));
    dispatch(setPeriodValueUI([1980, maxYear]));
  };

  const setGenresValueHandler = (itemId: any) => {
    dispatch(cleanUpPage());
    const result = genresValue.includes(itemId)
      ? genresValue.filter((id: any) => id !== itemId)
      : [...genresValue, itemId];
    dispatch(setGenresValue(result));
  };

  const setPeriodHandler = (periodValue: number[]) => {
    dispatch(setPeriodValueUI(periodValue));
  };

  const setPeriodRequestHandler = (nextPeriodValue: number[]) => {
    if (periodValue[0] === nextPeriodValue[0] && periodValue[1] === nextPeriodValue[1]) {
      return;
    }

    dispatch(cleanUpPage());
    dispatch(setPeriodValue(nextPeriodValue));
  };

  const queryHandler = () => {
    const query: IQuery = {
      language: languageHandler(i18n.language) as string,
      sort_by: "popularity.desc",
      include_video: true,
    };

    if (sortBy?.sort) {
      query.sort_by = sortBy.sort;
    }

    if (periodValue[0] !== 1980 || periodValue[1] !== maxYear) {
      query["release_date_gte"] = `${periodValue[0]}-01-01`;
      query["release_date_lte"] = `${periodValue[1]}-12-31`;
    }

    if (genresValue.length) {
      query.with_genres = genresValue.join("|");
    }

    return query;
  };

  const isMoviesTab = activeTab === "movies";
  const isActorsTab = activeTab === "actors";
  const isAISearch = isMoviesTab && !!searchValue;

  const {
    data: discoverData,
    isLoading: discoverLoading,
    isFetching: discoverFetching,
    error: discoverError,
  } = useGetSearchFilmsListQuery(
    {
      mainFilter: "/movies/discover",
      queries: {
        ...queryHandler(),
        page: page,
      },
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !isMoviesTab || isAISearch,
    },
  );

  const {
    data: aiData,
    isFetching: aiFetching,
    error: aiError,
  } = useGetAISearchFilmsListQuery(
    {
      q: searchValue,
      language: languageHandler(i18n.language) as string,
      limit: AI_SEARCH_PAGE_SIZE,
      page,
    },
    {
      skip: !isAISearch,
    },
  );

  const {
    data: actorsData,
    isFetching: actorsFetching,
    error: actorsError,
  } = useGetPersonsListQuery(
    {
      queries: {
        language: languageHandler(i18n.language) as string,
        sort_by: actorSortBy.sort,
        page: actorsPage,
        ...(searchValue ? { search: searchValue } : {}),
      },
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const data = isAISearch ? aiData : discoverData;
  const isFetching = isAISearch ? aiFetching : discoverFetching;
  const error = isAISearch ? aiError : discoverError;
  const activeFilterLabels = useMemo(() => {
    const labels: string[] = [];

    if (periodValueUI[0] !== 1980 || periodValueUI[1] !== maxYear) {
      labels.push(`${periodValueUI[0]}-${periodValueUI[1]}`);
    }

    genresValue.forEach((genreId: number) => {
      const genre = genresData.find((item) => item.id === genreId);

      if (genre?.name) {
        labels.push(t(genre.name));
      }
    });

    return labels;
  }, [genresValue, maxYear, periodValueUI, t]);

  useEffect(() => {
    if (isAISearch && !aiFetching && aiData?.page > prevPage && Array.isArray(aiData.results)) {
      dispatch(setDataToList(aiData.results));
      dispatch(setPrevPage(aiData?.page));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, aiFetching, isAISearch]);

  useEffect(() => {
    if (!isAISearch && !discoverFetching && discoverData?.page > prevPage) {
      dispatch(setDataToList(discoverData?.results));
      dispatch(setPrevPage(discoverData?.page));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, discoverFetching, isAISearch]);

  useEffect(() => {
    setActorsPage(1);
    setActorsPrevPage(0);
    setActorsList([]);
  }, [searchValue, actorSortBy.sort, i18n.language]);

  useEffect(() => {
    if (!actorsFetching && actorsData?.page > actorsPrevPage) {
      setActorsList((prevList) => {
        const nextList = actorsData?.page === 1 ? actorsData.results : [...prevList, ...actorsData.results];
        return nextList.filter(
          (item: any, index: number, self: any[]) => index === self.findIndex((actor) => actor.id === item.id),
        );
      });
      setActorsPrevPage(actorsData?.page);
    }
  }, [actorsFetching, actorsData, actorsPrevPage]);

  const handleClickChips = (item: { title: string; sort: string }) => {
    if (isActorsTab) {
      if (actorSortBy.sort === item.sort) return;

      setActorSortBy(item);
      return;
    }

    if (sortBy?.sort === item.sort) return;

    dispatch(cleanUpPage());
    setActiveChips(item.title);
    dispatch(setSortBy(item));
  };

  const setPageHandler = () => {
    dispatch(setPage(1));
  };

  const setActorsPageHandler = () => {
    setActorsPage((currentPage) => currentPage + 1);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(cleanUpPage());

    setInputValue(event.target.value);
    dispatch(setSearch(event.target.value));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchValue(inputValue);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  if (discoverLoading && !isAISearch) {
    return <Loader />;
  }

  const moviesTotal = data?.total_results || filmList?.length || 0;
  const actorsTotal = actorsData?.total_results || actorsData?.count || actorsList?.length || 0;
  const actorFavoriteIds = Array.isArray(actorFavoritesList?.data)
    ? actorFavoritesList.data
    : actorFavoritesList?.data?.results;

  return (
    <>
      <section>
        <Header source={isMobile ? "modal" : undefined} className={styles.headerWrapper} />
        <GradientBackdrop/>
        {windowSize !== "mobile" ? <SideBar /> : null}
        <div className={`${styles.container} ${isActorsTab ? styles.containerActors : ""}`}>
          <div className={`${styles.leftColumnWrapper} ${isActorsTab ? styles.leftColumnWrapperActors : ""}`}>
            <div className={styles.leftColumnTopContent}>
              <div className={styles.topControlsWrapper}>
                <div className={styles.tabsWrapper}>
                  <button
                    type="button"
                    className={`${styles.tabButton} ${isMoviesTab ? styles.tabButtonActive : ""}`}
                    onClick={() => setActiveTab("movies")}
                  >
                    <span className={styles.tabTitle}>{t("movies")}</span>
                    <span className={styles.tabCounter}>
                      <b>{moviesTotal.toLocaleString("uk-UA")}</b> {t("trailers")}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.tabButton} ${isActorsTab ? styles.tabButtonActive : ""}`}
                    onClick={() => setActiveTab("actors")}
                  >
                    <span className={styles.tabTitle}>{t("actors")}</span>
                    <span className={styles.tabCounter}>
                      <b>{actorsTotal.toLocaleString("uk-UA")}</b> {t("actor")}
                    </span>
                  </button>
                </div>
                <div className={styles.searchControlsWrapper}>
                  <div className={`${styles.searchFieldWrapper} ${isFocused ? styles.focused : ""}`}>
                    <SearchFieldIcon />
                    <Input
                      placeHolder={t("search")}
                      className={styles.input}
                      labelClassName={styles.label}
                      value={inputValue}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onChange={handleInputChange}
                    />
                  </div>
                  {(isActorsTab || !isAISearch) && (
                    <div className={styles.chipsListWrapper}>
                      {(isActorsTab ? actorSortList : movieSortList).map((item, index) => (
                        <button
                          key={index}
                          className={`${styles.chipsButton} ${
                            (isActorsTab ? actorSortBy.title : activeChips) === item.title
                              ? styles.chipsButtonActive
                              : ""
                          }`}
                          onClick={() => handleClickChips(item)}
                        >
                          <p className={styles.btnText}>{t(item.title)}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {isMoviesTab ? (
                <MoviesList
                  data={filmList}
                  error={error}
                  setPageHandler={setPageHandler}
                  isFetching={isFetching}
                  isLastData={
                    isAISearch
                      ? data?.results
                        ? data.results.length < AI_SEARCH_PAGE_SIZE
                        : false
                      : data?.page
                        ? data.page === data.total_pages
                        : false
                  }
                  likeHandler={likeHandler}
                  favouritesHandler={favouritesHandler}
                  title=""
                  likesList={likesList?.data?.results}
                  favoritesList={favoritesList?.data?.results}
                  isFavoritePage={false}
                  enableViewSwitcher={true}
                  viewStorageKey="hiviewer.search.viewMode"
                  activeFilterLabels={activeFilterLabels}
                />
              ) : (
                <ActorsList
                  data={actorsList}
                  error={actorsError}
                  setPageHandler={setActorsPageHandler}
                  isFetching={actorsFetching}
                  isLastData={actorsData?.page ? actorsData.page === actorsData.total_pages : false}
                  favoritesList={actorFavoriteIds}
                  favouritesHandler={actorFavouritesHandler}
                />
              )}
            </div>
          </div>
          {windowSize !== "mobile" && isMoviesTab && (
            <div className={styles.contentWrapper}>
              <PeriodModule
                fromPageFlag={true}
                setPeriodValue={setPeriodHandler}
                setPeriodValueCommitted={setPeriodRequestHandler}
                isMobile={!isMobile}
                className={styles.periodModule}
                period={periodValueUI}
                clearFiltersHandler={clearPeriodHandler}
              />
              <GenresModule
                fromPageFlag={true}
                setGenresValue={setGenresValueHandler}
                isMobile={!isMobile}
                className={styles.genresModule}
                genres={genresValue}
                clearFiltersHandler={clearGenresHandler}
              />
              <div className={styles.advertisement} />
            </div>
          )}
        </div>
        <Footer source={ROUTES.SEARCH} />
        {loginModal && <LoginModal />}
      </section>
    </>
  );
};

export default SearchPage;
